// Settings
import { GC_DEFAULTS, GC_PREFERENCES } from '@shell/utils/gc/gc-types';
import { PaginationSettings } from '@shell/types/resources/settings';

export const AGENT_CONFIGURATION_TYPES = {
  CLUSTER: 'cluster',
  FLEET:   'fleet'
} as const;

interface GlobalSettingRuleset {
  name: string,
  key?: string | number,
  factoryArg?: string | number | (string | number)[]
}

interface GlobalSetting {
  [key: string]: {
    alias?: string,
    canReset?: boolean,
    customFormatter?: string,
    from?: string,
    kind?: string,
    /**
     * How an `enum` setting is edited: a select (the default) or radio buttons. Only radio buttons support an empty
     * option (`''`), labelled by the `none` key in `advancedSettings.enum.<id>`.
     */
    display?: 'radio',
    options?: string[]
    readOnly?: boolean,
    agent?: typeof AGENT_CONFIGURATION_TYPES.CLUSTER | typeof AGENT_CONFIGURATION_TYPES.FLEET,
    /**
     * Function used from the form validation
     */
    ruleSet?: GlobalSettingRuleset[],
    warning?: string
  };
}

// Adapted from: https://github.com/rancher/ui/blob/08c379a9529f740666a704b52522a468986c3520/lib/shared/addon/utils/constants.js#L564
// Setting IDs
export const SETTING = {
  VERSION_RANCHER: 'server-version',
  VERSION_CLI:     'cli-version',
  VERSION_MACHINE: 'machine-version',
  VERSION_HELM:    'helm-version',
  CLI_URL:         {
    DARWIN:  'cli-url-darwin',
    WINDOWS: 'cli-url-windows',
    LINUX:   'cli-url-linux',
  },
  API_HOST: 'api-host',
  CA_CERTS: 'cacerts',

  // Allow the local cluster to be hidden
  HIDE_LOCAL_CLUSTER:                            'hide-local-cluster',
  AUTH_TOKEN_MAX_TTL_MINUTES:                    'auth-token-max-ttl-minutes',
  KUBECONFIG_GENERATE_TOKEN:                     'kubeconfig-generate-token',
  KUBECONFIG_DEFAULT_TOKEN_TTL_MINUTES:          'kubeconfig-default-token-ttl-minutes',
  KUBECONFIG_PREFER_RANCHER_PROXY:               'kubeconfig-prefer-rancher-proxy',
  ENGINE_ISO_URL:                                'engine-iso-url',
  FIRST_LOGIN:                                   'first-login',
  INGRESS_IP_DOMAIN:                             'ingress-ip-domain',
  SERVER_URL:                                    'server-url',
  RKE_METADATA_CONFIG:                           'rke-metadata-config',
  EULA_AGREED:                                   'eula-agreed',
  AUTH_USER_INFO_MAX_AGE_SECONDS:                'auth-user-info-max-age-seconds',
  AUTH_USER_SESSION_IDLE_TTL_MINUTES:            'auth-user-session-idle-ttl-minutes',
  AUTH_USER_SESSION_TTL_MINUTES:                 'auth-user-session-ttl-minutes',
  AUTH_USER_INFO_RESYNC_CRON:                    'auth-user-info-resync-cron',
  AUTH_LOCAL_VALIDATE_DESC:                      'auth-password-requirements-description',
  PASSWORD_MIN_LENGTH:                           'password-min-length', // CATTLE_PASSWORD_MIN_LENGTH
  UI_DASHBOARD_INDEX:                            'ui-dashboard-index',
  UI_DASHBOARD_HARVESTER_LEGACY_PLUGIN:          'ui-dashboard-harvester-legacy-plugin',
  UI_OFFLINE_PREFERRED:                          'ui-offline-preferred',
  SYSTEM_DEFAULT_REGISTRY:                       'system-default-registry',
  SYSTEM_DEFAULT_REGISTRY_PULL_SECRETS:          'system-default-registry-pull-secrets',
  UI_ISSUES:                                     'ui-issues',
  PL:                                            'ui-pl',
  PL_RANCHER_VALUE:                              'rancher',
  SUPPORTED:                                     'has-support',
  BANNERS:                                       'ui-banners',
  ISSUES:                                        'ui-issues',
  BRAND:                                         'ui-brand',
  LOGO_LIGHT:                                    'ui-logo-light',
  LOGO_DARK:                                     'ui-logo-dark',
  BANNER_LIGHT:                                  'ui-banner-light',
  BANNER_DARK:                                   'ui-banner-dark',
  LOGIN_BACKGROUND_LIGHT:                        'ui-login-background-light',
  LOGIN_BACKGROUND_DARK:                         'ui-login-background-dark',
  PRIMARY_COLOR:                                 'ui-primary-color',
  LINK_COLOR:                                    'ui-link-color',
  COMMUNITY_LINKS:                               'ui-community-links',
  FAVICON:                                       'ui-favicon',
  UI_PERFORMANCE:                                'ui-performance',
  UI_CUSTOM_LINKS:                               'ui-custom-links',
  UI_SUPPORTED_K8S_VERSIONS:                     'ui-k8s-supported-versions-range',
  /**
   * Allow the backend to force a light/dark theme. Used in non-rancher world and results in the theme used
   * both pre and post log in. If not present defaults to the usual process
   */
  THEME:                                         'ui-theme',
  SYSTEM_NAMESPACES:                             'system-namespaces',
  /**
   * Cluster Agent configuration
   */
  CLUSTER_AGENT_DEFAULT_AFFINITY:                'cluster-agent-default-affinity',
  FLEET_AGENT_DEFAULT_AFFINITY:                  'fleet-agent-default-affinity',
  /**
   * manage rancher repositories in extensions (official, partners repos)
  */
  ADD_EXTENSION_REPOS_BANNER_DISPLAY:            'display-add-extension-repos-banner',
  AGENT_TLS_MODE:                                'agent-tls-mode',
  /**
   * User retention settings
   */
  USER_RETENTION_CRON:                           'user-retention-cron',
  USER_RETENTION_DRY_RUN:                        'user-retention-dry-run',
  USER_LAST_LOGIN_DEFAULT:                       'user-last-login-default',
  DISABLE_INACTIVE_USER_AFTER:                   'disable-inactive-user-after',
  DELETE_INACTIVE_USER_AFTER:                    'delete-inactive-user-after',
  K3S_UPGRADER_UNINSTALL_CONCURRENCY:            'k3s-based-upgrader-uninstall-concurrency',
  SYSTEM_AGENT_UPGRADER_INSTALL_CONCURRENCY:     'system-agent-upgrader-install-concurrency',
  IMPORTED_CLUSTER_VERSION_MANAGEMENT:           'imported-cluster-version-management',
  CLUSTER_AGENT_DEFAULT_PRIORITY_CLASS:          'cluster-agent-default-priority-class',
  CLUSTER_AGENT_DEFAULT_POD_DISTRIBUTION_BUDGET: 'cluster-agent-default-pod-disruption-budget',
  FLEET_AGENT_DEFAULT_PRIORITY_CLASS:            'fleet-agent-default-priority-class',
  FLEET_AGENT_DEFAULT_POD_DISTRIBUTION_BUDGET:   'fleet-agent-default-pod-disruption-budget',
  KEV2_OPERATORS:                                'kev2-operators',
  /**
   * Dynamic Content settings
   */
  DYNAMIC_CONTENT_ENABLED:                       'ui-content-enabled',
  DYNAMIC_CONTENT_ENDPOINT:                      'ui-content-endpoint',
  IMPORTED_CLUSTER_DAY2_OPS_DEFAULT:             'imported-cluster-day2-ops-enabled',
  DELETE_MACHINE_ON_FAILURE_AFTER:               'delete-machine-on-failure-after',
  /**
   * Rancher's catalog mode (`CATTLE_SYSTEM_CATALOG`), either `external` or `bundled`. `bundled` means Rancher only
   * serves bundled/airgap charts. Read-only (set by the backend), so not in ALLOWED_SETTINGS.
   */
  SYSTEM_CATALOG:                                'system-catalog',
  /**
   * Controls the SUSE Application Collection features in the UI: `true` always shows them, `false` never does and
   * empty (the default) falls back to SYSTEM_CATALOG, showing them unless it is `bundled`.
   */
  UI_APPCO_ENABLED:                              'ui-appco-enabled',
} as const;

// These are the settings that are allowed to be edited via the UI
export const ALLOWED_SETTINGS: GlobalSetting = {
  [SETTING.CA_CERTS]:            { kind: 'multiline', readOnly: true },
  [SETTING.ENGINE_ISO_URL]:      {},
  [SETTING.PASSWORD_MIN_LENGTH]: {
    kind:    'integer',
    ruleSet: [
      {
        name:       'betweenValues',
        key:        'Password',
        factoryArg: [2, 256]
      },
      {
        name: 'isInteger',
        key:  'Password',
      },
      {
        name: 'isPositive',
        key:  'Password',
      },
      {
        name: 'isOctal',
        key:  'Password',
      }
    ],
  },
  [SETTING.INGRESS_IP_DOMAIN]:                    {},
  [SETTING.AUTH_USER_INFO_MAX_AGE_SECONDS]:       {},
  [SETTING.AUTH_USER_SESSION_IDLE_TTL_MINUTES]:   {},
  [SETTING.AUTH_USER_SESSION_TTL_MINUTES]:        {},
  [SETTING.AUTH_TOKEN_MAX_TTL_MINUTES]:           {},
  [SETTING.KUBECONFIG_GENERATE_TOKEN]:            { kind: 'boolean' },
  [SETTING.KUBECONFIG_DEFAULT_TOKEN_TTL_MINUTES]: { kind: 'integer' },
  [SETTING.KUBECONFIG_PREFER_RANCHER_PROXY]:      { kind: 'enum', options: ['false', 'true'] },
  [SETTING.AUTH_USER_INFO_RESYNC_CRON]:           {},
  [SETTING.SERVER_URL]:                           { kind: 'url', canReset: true },
  [SETTING.RKE_METADATA_CONFIG]:                  { kind: 'json' },
  [SETTING.SYSTEM_DEFAULT_REGISTRY]:              {},
  [SETTING.SYSTEM_DEFAULT_REGISTRY_PULL_SECRETS]: {},
  [SETTING.UI_DASHBOARD_INDEX]:                   {},
  [SETTING.UI_OFFLINE_PREFERRED]:                 {
    kind:    'enum',
    options: ['dynamic', 'true', 'false']
  },
  [SETTING.UI_APPCO_ENABLED]: {
    kind:     'enum',
    display:  'radio',
    options:  ['true', 'false', ''],
    canReset: true,
  },
  [SETTING.BRAND]:              { canReset: true },
  [SETTING.HIDE_LOCAL_CLUSTER]: { kind: 'boolean' },
  [SETTING.AGENT_TLS_MODE]:     {
    kind:    'enum',
    options: ['strict', 'system-store'],
    warning: 'agent-tls-mode'
  },
  [SETTING.SYSTEM_AGENT_UPGRADER_INSTALL_CONCURRENCY]: {
    kind:    'integer',
    ruleSet: [{ name: 'minValue', factoryArg: 1 }]
  },
  [SETTING.K3S_UPGRADER_UNINSTALL_CONCURRENCY]: {
    kind:    'integer',
    ruleSet: [{ name: 'minValue', factoryArg: 1 }]
  },
  [SETTING.IMPORTED_CLUSTER_VERSION_MANAGEMENT]:           { kind: 'boolean' },
  [SETTING.IMPORTED_CLUSTER_DAY2_OPS_DEFAULT]:             { kind: 'boolean' },
  // Configuration setup for agent configuration. Setting this up will activate the specific banner configuration.
  [SETTING.CLUSTER_AGENT_DEFAULT_PRIORITY_CLASS]:          { kind: 'json', agent: AGENT_CONFIGURATION_TYPES.CLUSTER },
  [SETTING.CLUSTER_AGENT_DEFAULT_POD_DISTRIBUTION_BUDGET]: { kind: 'json', agent: AGENT_CONFIGURATION_TYPES.CLUSTER },
  [SETTING.FLEET_AGENT_DEFAULT_PRIORITY_CLASS]:            { kind: 'json', agent: AGENT_CONFIGURATION_TYPES.FLEET },
  [SETTING.FLEET_AGENT_DEFAULT_POD_DISTRIBUTION_BUDGET]:   { kind: 'json', agent: AGENT_CONFIGURATION_TYPES.FLEET },
  [SETTING.DELETE_MACHINE_ON_FAILURE_AFTER]:               { kind: 'string' },
};

/**
 * Show settings in a special cluster provisioning section
 *
 * These should probably be an option in the maps above...
 */
export const PROVISIONING_SETTINGS = [
  SETTING.ENGINE_ISO_URL,
  SETTING.RKE_METADATA_CONFIG,
  SETTING.K3S_UPGRADER_UNINSTALL_CONCURRENCY,
  SETTING.IMPORTED_CLUSTER_VERSION_MANAGEMENT,
  SETTING.CLUSTER_AGENT_DEFAULT_PRIORITY_CLASS,
  SETTING.CLUSTER_AGENT_DEFAULT_POD_DISTRIBUTION_BUDGET,
  SETTING.FLEET_AGENT_DEFAULT_PRIORITY_CLASS,
  SETTING.FLEET_AGENT_DEFAULT_POD_DISTRIBUTION_BUDGET,
  SETTING.IMPORTED_CLUSTER_DAY2_OPS_DEFAULT,
  SETTING.DELETE_MACHINE_ON_FAILURE_AFTER,
];

/**
 * Settings on how to handle warnings returning in api responses, specifically which to show as growls
 */
export interface PerfSettingsWarningHeaders {
  /**
   * Warning is a string containing multiple entries. This determines how they are split up
   *
   * See https://github.com/kubernetes/enhancements/tree/master/keps/sig-api-machinery/1693-warnings#design-details
   */
  separator: string,
  /**
   * Show warnings in a notification if they're not in this block list
   */
  notificationBlockList: string[]
}

export interface PerfSettingsKubeApi {
  /**
   * Settings related to the response header `warnings` value
   */
  warningHeader: PerfSettingsWarningHeaders
}

export interface PerfSettings {
  inactivity: {
    enabled: boolean;
    threshold: number;
  };
  incrementalLoading: {
    enabled: boolean;
    threshold: number;
  };
  manualRefresh: {
    enabled: boolean;
    threshold: number;
  };
  disableWebsocketNotification: boolean;
  garbageCollection: GC_PREFERENCES;
  forceNsFilterV2: any;
  advancedWorker: {};
  kubeAPI: PerfSettingsKubeApi;
  serverPagination: PaginationSettings;
}

export const DEFAULT_PERF_SETTING: PerfSettings = {
  inactivity: {
    enabled:   false,
    threshold: 900,
  },
  incrementalLoading: {
    enabled:   true,
    threshold: 1500,
  },
  manualRefresh: {
    enabled:   false,
    threshold: 1500,
  },
  disableWebsocketNotification: true,
  garbageCollection:            GC_DEFAULTS,
  forceNsFilterV2:              { enabled: false },
  advancedWorker:               { enabled: false },
  kubeAPI:                      {
    /**
     * Settings related to the response header `warnings` value
     */
    warningHeader: {
      /**
       * Warning is a string containing multiple entries. This determines how they are split up
       *
       * See https://github.com/kubernetes/enhancements/tree/master/keps/sig-api-machinery/1693-warnings#design-details
       */
      separator:             '299 - ',
      /**
       * Show warnings in a notification if they're not in this block list
       */
      notificationBlockList: ['299 - unknown field']
    }
  },
  serverPagination: {
    useDefaultStores:          true,
    stores:                    undefined,
    resourceChangesDebounceMs: 4000,
  }
};
